import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { resourceService } from "@/services";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

function buildBreadcrumbs(
  pathname: string,
  t: (key: string) => string,
  resourceName?: string,
): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [];

  // Dashboard
  if (pathname === "/" || pathname === "/dashboard") {
    items.push({ label: t("page.title.dashboard") });
    return items;
  }

  // VMs
  if (
    pathname === "/resources/vms" ||
    pathname.match(/^\/resources\/[a-f0-9-]+$/)
  ) {
    items.push({ label: t("sidebar.navigation.compute") }); // no link — group label
    items.push({
      label: t("sidebar.navigation.virtualMachines"),
      path: "/resources/vms",
    });
    if (resourceName) {
      items.push({ label: resourceName });
    }
    return items;
  }

  // Create VM
  if (pathname === "/resources/create") {
    items.push({ label: t("sidebar.navigation.compute") });
    items.push({
      label: t("sidebar.navigation.virtualMachines"),
      path: "/resources/vms",
    });
    items.push({ label: t("page.title.createVM") });
    return items;
  }

  // Disks
  if (pathname === "/resources/disks") {
    items.push({ label: t("sidebar.navigation.data") });
    items.push({ label: t("page.title.disks") });
    return items;
  }
  if (pathname.match(/^\/resources\/disks\/[a-f0-9-]+$/)) {
    items.push({ label: t("sidebar.navigation.data") });
    items.push({ label: t("page.title.disks"), path: "/resources/disks" });
    if (resourceName) {
      items.push({ label: resourceName });
    }
    return items;
  }

  // Networks
  if (pathname === "/resources/networks") {
    items.push({ label: t("sidebar.navigation.network") });
    items.push({ label: t("page.title.networks") });
    return items;
  }
  if (pathname.match(/^\/resources\/networks\/[a-f0-9-]+$/)) {
    items.push({ label: t("sidebar.navigation.network") });
    items.push({
      label: t("page.title.networks"),
      path: "/resources/networks",
    });
    if (resourceName) {
      items.push({ label: resourceName });
    }
    return items;
  }

  // Storage
  if (pathname === "/resources/storage") {
    items.push({ label: t("sidebar.navigation.data") });
    items.push({ label: t("sidebar.navigation.objectStorage") });
    return items;
  }
  if (pathname.match(/^\/resources\/storage\/create$/)) {
    items.push({ label: t("sidebar.navigation.data") });
    items.push({
      label: t("sidebar.navigation.objectStorage"),
      path: "/resources/storage",
    });
    items.push({ label: t("page.title.createStorage") });
    return items;
  }
  if (pathname.match(/^\/resources\/storage\/[a-f0-9-]+$/)) {
    items.push({ label: t("sidebar.navigation.data") });
    items.push({
      label: t("sidebar.navigation.objectStorage"),
      path: "/resources/storage",
    });
    if (resourceName) {
      items.push({ label: resourceName });
    }
    return items;
  }

  // Orders
  if (pathname === "/orders") {
    items.push({ label: t("sidebar.navigation.commerce") });
    items.push({ label: t("sidebar.navigation.orders") });
    return items;
  }
  if (pathname.match(/^\/orders\/[a-f0-9-]+$/)) {
    items.push({ label: t("sidebar.navigation.commerce") });
    items.push({ label: t("sidebar.navigation.orders"), path: "/orders" });
    items.push({ label: t("page.title.orderDetail") });
    return items;
  }

  // Billing
  if (pathname === "/billing") {
    items.push({ label: t("sidebar.navigation.commerce") });
    items.push({ label: t("sidebar.navigation.billing") });
    return items;
  }
  if (pathname.startsWith("/billing/")) {
    items.push({ label: t("sidebar.navigation.commerce") });
    items.push({ label: t("sidebar.navigation.billing"), path: "/billing" });
    const sub = pathname.replace("/billing/", "");
    const billingSubtitles: Record<string, string> = {
      balance: t("page.title.balance"),
      "add-balance": t("page.title.addBalance"),
      deposit: t("page.title.deposit"),
      "payment-history": t("page.title.paymentHistory"),
      invoices: t("page.title.invoices"),
      "payment-methods": t("page.title.paymentMethods"),
    };
    if (billingSubtitles[sub]) {
      items.push({ label: billingSubtitles[sub] });
    } else if (sub.startsWith("payment/")) {
      items.push({ label: t("page.title.paymentStatus") });
    }
    return items;
  }

  // Settings
  if (pathname.startsWith("/settings")) {
    items.push({ label: t("page.title.settings") });
    const sub = pathname.replace("/settings/", "");
    const settingsSubtitles: Record<string, string> = {
      profile: t("page.title.profile"),
      security: t("page.title.security"),
      preferences: t("page.title.preferences"),
      "api-keys": t("page.title.apiKeys"),
    };
    if (settingsSubtitles[sub]) {
      items.push({ label: settingsSubtitles[sub] });
    }
    return items;
  }

  // Logs
  if (pathname === "/logs") {
    items.push({ label: t("sidebar.navigation.observability") });
    items.push({ label: t("sidebar.navigation.logs") });
    return items;
  }

  // Account
  if (pathname === "/account/connected-accounts") {
    items.push({ label: t("userMenu.connectedAccounts") });
    return items;
  }

  // Fallback: use path segments with capitalization
  const segments = pathname.split("/").filter(Boolean);
  for (const segment of segments) {
    if (segment.match(/^[a-f0-9-]{36}$/)) continue; // skip UUIDs
    items.push({
      label:
        segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " "),
    });
  }
  return items;
}

export function Breadcrumbs() {
  const location = useLocation();
  const { t } = useTranslation("common");
  const params = useParams<{ resourceId?: string }>();
  const translate = t as (key: string) => string;

  // Fetch resource name for detail pages
  const isDetailPage =
    location.pathname.match(
      /^\/resources\/(?!vms|disks|networks|storage|create)[a-f0-9-]+$/,
    ) ||
    location.pathname.match(
      /^\/resources\/(disks|networks|storage)\/[a-f0-9-]+$/,
    );

  // Same key as the detail pages on purpose. Only the name is needed here, but a private key
  // ("breadcrumb-resource") shares no cache with them, so entering a detail page fired the very
  // same GET /resources/{id} twice. With the key shared the two observers dedupe into one
  // request, and `select` keeps this component re-rendering on the name alone.
  const { data: resource } = useQuery({
    queryKey: ["resources", "detail", params.resourceId],
    queryFn: () => resourceService.get(params.resourceId!),
    enabled: !!(isDetailPage && params.resourceId),
    staleTime: 60000,
    select: (data) => data.name,
  });

  const items = buildBreadcrumbs(
    location.pathname,
    translate,
    resource as string | undefined,
  );

  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <div
            key={`${item.label}-${index}`}
            className="flex items-center gap-2"
          >
            {/* Separator sits BETWEEN crumbs only, so the first label starts flush
                with the page gutter and lines up with the page title below it. */}
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            {isLast ? (
              // Current page: the only crumb rendered at full ink + medium weight.
              <span
                aria-current="page"
                className="font-medium text-foreground truncate max-w-[150px] md:max-w-none"
              >
                {item.label}
              </span>
            ) : !item.path ? (
              // Group label (a sidebar section, not a route) — an ancestor, so it
              // must read like one rather than like the current page.
              <span className="text-muted-foreground truncate max-w-[100px] md:max-w-none">
                {item.label}
              </span>
            ) : (
              <Link
                to={item.path}
                className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[100px] md:max-w-none"
              >
                {item.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}
